import { useCallback, useEffect, useState } from 'react';
import { call } from '@/api.ts';
import type { Channel, PreviewGrant } from '@/api.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';
import { refusal, roleRefusal } from '@/lib/refusal.ts';
import { canWrite, useAuthenticated } from '@/session.tsx';

/**
 * Whether a channel may have a link minted for it.
 *
 * **This is the safety property of the page, and it used to be enforced by only listing preview
 * channels at all.** The design shows every channel — an operator wants to see `live` and `beta`
 * beside the branches — so the rule moved from the query to the button, where it has to be stated
 * per row rather than implied by an absence.
 *
 * Two ways to fail it. A link for `live` or `beta` gates nothing while looking like access
 * control: every visitor already composes from those. A link for a channel pointing at nothing
 * composes nothing, so it reads as broken rather than as refused.
 */
export function canMintLink(channel: Channel): boolean {
  return channel.name.startsWith('preview/') && Boolean(channel.snapshotId);
}

/** Why the button is disabled, in the words an operator needs. `null` when it is not. */
export function mintRefusal(channel: Channel): string | null {
  if (!channel.name.startsWith('preview/')) {
    return `Everyone already sees ${channel.name} — a link for it would gate nothing.`;
  }
  if (!channel.snapshotId) return 'This channel points at nothing, so a link would compose nothing.';
  return null;
}

/**
 * Channels in the order an operator reads them: what is live, what is rolling out, then branches.
 *
 * Alphabetical would put `beta` above `live`, which inverts the two rows that matter most.
 */
export function orderChannels(channels: readonly Channel[]): Channel[] {
  const rank = (name: string) => (name === 'live' ? 0 : name === 'beta' ? 1 : 2);
  return [...channels].sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
}

/**
 * The grants that still open a channel.
 *
 * A revoked grant stays in the listing — the audit trail is the point — but it opens nothing, so
 * counting it would tell an operator a link is live when it is not. That is the error that matters
 * here: it reads as "someone can still see this branch" when nobody can, and the opposite mistake
 * would be worse still.
 */
export function openGrants(grants: readonly PreviewGrant[], channel: string): PreviewGrant[] {
  return grants.filter((grant) => grant.channel === channel && !grant.revokedAt);
}

/** What the "Who sees it" column says, per channel. */
export function audienceOf(channel: Channel, openLinks: number): string {
  if (channel.name === 'live') return 'Everyone';
  if (channel.name === 'beta') {
    const rules = channel.rules?.length ?? 0;
    return rules === 0 ? 'Nobody — no targeting rules' : `${rules} targeting ${rules === 1 ? 'rule' : 'rules'}`;
  }
  return openLinks === 0 ? 'Nobody yet — no link minted' : `${openLinks} open ${openLinks === 1 ? 'link' : 'links'}`;
}

/**
 * Channels, the grants that open them, and the promotion that ends a rollout.
 *
 * The console half of the staged/live warning. The in-page ribbon labels each fragment for whoever
 * opens the link; this is where the platform team sees what is in flight, mints a link, takes one
 * back, and promotes a branch everyone will get.
 */
export function PreviewsPage() {
  const session = useAuthenticated();
  const writable = canWrite(session.role);
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const [grants, setGrants] = useState<PreviewGrant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Shown once, never refetchable: the API returns a secret at mint and never again. */
  const [minted, setMinted] = useState<{ channel: string; secret: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);

  const load = useCallback(async () => {
    const [channelList, grantList] = await Promise.all([
      call<{ items: Channel[] }>(session, '/channels'),
      call<{ items: PreviewGrant[] }>(session, '/previews'),
    ]);
    setChannels(orderChannels(channelList.items));
    setGrants(grantList.items);
  }, [session]);

  useEffect(() => {
    let live = true;
    load().catch((cause: Error) => { if (live) setError(cause.message); });
    return () => { live = false; };
  }, [load]);

  async function mint(channel: string) {
    setBusy(channel);
    setError(null);
    try {
      const created = await call<{ grant: PreviewGrant & { secret: string } }>(session, '/previews', {
        method: 'POST',
        body: JSON.stringify({ channel }),
      });
      setMinted({ channel, secret: created.grant.secret });
      await load();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function revoke(grant: PreviewGrant) {
    setBusy(grant.hash);
    setError(null);
    try {
      await call(session, `/previews/${grant.hash}`, { method: 'DELETE' });
      await load();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const live = channels?.find((channel) => channel.name === 'live') ?? null;
  const beta = channels?.find((channel) => channel.name === 'beta') ?? null;

  /**
   * Promotion is a pointer move, guarded by the version the page was rendered from.
   *
   * `expectedVersion` is what makes two operators promoting at once safe: the second write is
   * refused rather than applied on top, and the refusal is recorded in the audit log as its own
   * entry. Sending the version we last read is the whole mechanism — omitting it would make this
   * last-write-wins.
   */
  async function promote() {
    if (!live || !beta?.snapshotId) return;
    setBusy('promote');
    setError(null);
    try {
      await call(session, `/channels/live`, {
        method: 'PUT',
        body: JSON.stringify({ snapshotId: beta.snapshotId, expectedVersion: live.version }),
      });
      setPromoting(false);
      await load();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const promotable = Boolean(live && beta?.snapshotId && beta.snapshotId !== live.snapshotId);

  return (
    <section className="grid gap-8 lg:grid-cols-[1fr_400px] lg:gap-11">
      <div className="grid min-w-0 content-start gap-5">
        <header>
          <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">Channels</h1>
          <p className="text-muted-foreground text-sm">
            A preview channel serves nobody until someone opens a link for it. Anyone holding an
            unexpired link sees that branch, so treat one like a password.
          </p>
        </header>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>That did not work</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {!channels ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((row) => <Skeleton key={row} className="h-4 w-full" />)}
          </div>
        ) : channels.length === 0 ? (
          <EmptyState
            title="No channels yet"
            description="Publishing a snapshot creates the live channel. Publish to a preview/{branch} channel to share work before it ships."
          />
        ) : (
          <Table>
            <colgroup>
              <col className="w-[220px]" />
              <col className="w-[150px]" />
              <col />
              <col className="w-[150px]" />
            </colgroup>
            <TableHeader className="[&_tr]:border-foreground [&_tr]:border-b">
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead>Snapshot</TableHead>
                <TableHead>Who sees it</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {channels.map((channel) => {
                const open = openGrants(grants ?? [], channel.name);
                const whyNot = mintRefusal(channel);
                return (
                  <TableRow key={channel.name}>
                    <TableCell className="font-mono text-[13px] font-semibold">{channel.name}</TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {channel.snapshotId ?? (
                        // A channel pointing at nothing composes nothing, so a link for it would
                        // look broken rather than refused. Say so before someone mints one.
                        <span>nothing published</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[13px]">
                      {audienceOf(channel, open.length)}
                      {open.length > 0 && (
                        <span className="ml-2 align-middle">
                          <Badge variant="secondary">{open.length} open</Badge>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy === channel.name || !canMintLink(channel) || !writable}
                        title={refusal(roleRefusal(session.role, 'mint links', writable), whyNot)}
                        onClick={() => void mint(channel.name)}
                      >
                        Mint link
                      </Button>
                      {open.map((grant) => (
                        <Button
                          key={grant.hash}
                          size="sm"
                          variant="ghost"
                          disabled={busy === grant.hash || !writable}
                          title={refusal(roleRefusal(session.role, 'revoke links', writable))}
                          onClick={() => void revoke(grant)}
                        >
                          Revoke
                        </Button>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <div>
          <Button
            disabled={!promotable || !writable || busy === 'promote'}
            title={refusal(
              roleRefusal(session.role, 'promote a channel', writable),
              promotable ? null : 'There is nothing on beta that live is not already serving.',
            )}
            onClick={() => setPromoting(true)}
          >
            Promote beta to live
          </Button>
        </div>
      </div>

      {/* The rail: what promotion will do, the secret that was just minted, and what a previewed
          page actually looks like. Hairlines between them rather than three cards. */}
      <aside className="divide-border grid content-start gap-6 divide-y">
        <div className="grid gap-2">
          <h2 className="text-[11px] font-medium tracking-[0.12em] uppercase">Before you promote</h2>
          <p className="text-[13px] leading-relaxed">
            Promoting points <code className="font-mono">live</code> at the snapshot{' '}
            <code className="font-mono">beta</code> serves. Every visitor gets it on their next
            request, once the 15-second config cache expires.
          </p>
          <p className="text-muted-foreground text-[12.5px] leading-relaxed">
            The move carries the version this page was read at, so two operators promoting at once
            cannot overwrite each other — the second is refused, and the refusal is its own audit
            entry rather than a silent loss.
          </p>
        </div>

        {minted ? (
          <div className="grid gap-2 pt-6">
            <h2 className="text-[11px] font-medium tracking-[0.12em] uppercase">Link just minted</h2>
            <p className="text-[13px]">
              For <code className="font-mono">{minted.channel}</code>. Copy it now — it is shown
              once and cannot be shown again, because the API stores only a hash.
            </p>
            <code className="bg-muted px-2 py-1.5 font-mono text-xs break-all">
              {`/__braid/preview?grant=${minted.secret}`}
            </code>
            <p className="text-muted-foreground text-[12.5px] leading-relaxed">
              Open it on the tenant host. It sets a cookie and redirects, so the secret does not
              stay in the address bar or in anyone's history.
            </p>
          </div>
        ) : null}

        <div className="grid gap-2 pt-6">
          <h2 className="text-[11px] font-medium tracking-[0.12em] uppercase">What a previewed page looks like</h2>
          <p className="text-[13px] leading-relaxed">
            Every fragment is labelled <strong>staged</strong> or <strong>live data</strong> in the
            page itself. A fragment with no staging endpoint is on <em>live data</em> even inside a
            preview — it reads and writes production.
          </p>
          <p className="text-muted-foreground text-[12.5px] leading-relaxed">
            Shells you host yourself get the same information as an{' '}
            <code className="font-mono">x-braid-staged</code> response header, since this platform
            does not write into a document it does not own.
          </p>
        </div>
      </aside>

      <ConfirmDialog
        open={promoting}
        onOpenChange={setPromoting}
        title="Promote beta to live?"
        description={
          <>
            Every visitor will compose from <code className="font-mono">{beta?.snapshotId}</code>{' '}
            within about fifteen seconds. Rolling back is another pointer move, not an undo.
          </>
        }
        confirmLabel="Promote"
        busy={busy === 'promote'}
        onConfirm={() => void promote()}
      />
    </section>
  );
}
